import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, t } from "../../lib/api";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { Empty, ErrorNote } from "../../components/ui";
import type { Engagement } from "../Engagement";

interface QSet {
  id: string;
  name: string;
  sourceLang: string;
  moduleKey: string | null;
  questions: Array<{
    id: string;
    topic: string;
    text: string;
    probes: string[];
    order: number;
    translations: Array<{ lang: string; text: string; verified: boolean; machine: boolean; hasAudio: boolean }>;
  }>;
}

/**
 * The guide is topics to cover, not a script. Verified translations are
 * spoken from pre-generated audio, so prepared questions play instantly.
 */
export function GuideTab({ engagement }: { engagement: Engagement }) {
  const { tenant } = useParams();
  const { config } = useSession();
  const sets = useAsync(() => api.get<QSet[]>(`${t(tenant!)}/engagements/${engagement.id}/question-sets`), [tenant, engagement.id]);
  const lead = engagement.role === "admin" || engagement.role === "lead";
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [newSet, setNewSet] = useState("");
  const targetLangs = engagement.intervieweeLanguages.filter((l) => !engagement.interviewerLanguages.includes(l));
  const lname = (c: string) => config.languages.find((l) => l.code === c)?.name ?? c;

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await sets.reload();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  };

  const total = (sets.data ?? []).reduce((a, s) => a + s.questions.length, 0);
  const verified = (sets.data ?? []).reduce((a, s) => a + s.questions.filter((q) => targetLangs.every((l) => q.translations.find((x) => x.lang === l)?.verified)).length, 0);

  return (
    <div className="stack" style={{ gap: 20 }}>
      <div className="row wrap">
        <p className="muted">
          {total} questions · {verified} with verified translations in every interviewee language
        </p>
        <span className="spacer" />
        {lead && (
          <>
            <button className="btn" disabled={!!busy} onClick={() => act("translate", () => api.post(`${t(tenant!)}/engagements/${engagement.id}/question-sets/translate`))}>
              {busy === "translate" ? "Translating…" : "Draft missing translations"}
            </button>
            <button className="btn" disabled={!!busy} onClick={() => act("audio", () => api.post(`${t(tenant!)}/engagements/${engagement.id}/prepare-audio`))}>
              Prepare spoken audio
            </button>
          </>
        )}
      </div>
      <ErrorNote error={error} />
      {sets.data?.length === 0 && (
        <div className="panel">
          <Empty title="No question guide yet">Add a question set, or turn on a focus review in Settings to bring in its questions.</Empty>
        </div>
      )}
      {(sets.data ?? []).map((s) => (
        <section className="panel" key={s.id}>
          <div className="panel-head">
            <h3>{s.name}</h3>
            {s.moduleKey && <span className="pill ochre">Focus review</span>}
            <p>Written in {lname(s.sourceLang)}</p>
          </div>
          <div className="stack tight">
            {s.questions.map((q) => (
              <QuestionRow key={q.id} q={q} engagement={engagement} langs={targetLangs} lead={lead} onChange={() => void sets.reload()} />
            ))}
          </div>
          {lead && <AddQuestion setId={s.id} engagement={engagement} onAdded={() => void sets.reload()} />}
        </section>
      ))}
      {lead && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void act("set", async () => {
              await api.post(`${t(tenant!)}/engagements/${engagement.id}/question-sets`, { name: newSet, sourceLang: engagement.interviewerLanguages[0] });
              setNewSet("");
            });
          }}
        >
          <input className="input" style={{ maxWidth: 360 }} placeholder="New question set, e.g. Finance team" value={newSet} onChange={(e) => setNewSet(e.target.value)} required />
          <button className="btn">Add question set</button>
        </form>
      )}
    </div>
  );
}

function QuestionRow({ q, engagement, langs, lead, onChange }: { q: QSet["questions"][number]; engagement: Engagement; langs: string[]; lead: boolean; onChange: () => void }) {
  const { tenant } = useParams();
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState("");
  const save = async (lang: string, verifiedFlag: boolean, value: string) => {
    await api.put(`${t(tenant!)}/engagements/${engagement.id}/questions/${q.id}/translations`, { lang, text: value, verified: verifiedFlag });
    setEditing(null);
    onChange();
  };
  return (
    <div className="item" style={{ padding: "12px 14px" }}>
      <div className="row" style={{ alignItems: "baseline" }}>
        <span className="muted xs" style={{ minWidth: 120 }}>
          {q.topic}
        </span>
        <span style={{ fontWeight: 560 }}>{q.text}</span>
      </div>
      {q.probes.length > 0 && <div className="muted xs" style={{ marginLeft: 132 }}>Probe: {q.probes.join(" · ")}</div>}
      {langs.map((lang) => {
        const tr = q.translations.find((x) => x.lang === lang);
        return (
          <div key={lang} className="row small" style={{ marginLeft: 132, marginTop: 6, alignItems: "flex-start" }}>
            <span className="faint" style={{ minWidth: 48 }}>{lang}</span>
            {editing === lang ? (
              <form
                className="row"
                style={{ flex: 1 }}
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  void save(lang, true, text);
                }}
              >
                <input className="input" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
                <button className="btn small primary">Save as verified</button>
                <button type="button" className="btn small ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <span style={{ flex: 1 }} className={tr ? "" : "faint"}>
                  {tr?.text ?? "No translation yet"}
                </span>
                {tr?.verified ? (
                  <span className="pill green">{tr.hasAudio ? "Verified · audio ready" : "Verified"}</span>
                ) : tr ? (
                  <span className="pill amber">{tr.machine ? "Machine draft" : "Not verified"}</span>
                ) : null}
                {lead && tr && !tr.verified && (
                  <button className="btn small" onClick={() => void save(lang, true, tr.text)}>
                    Verify
                  </button>
                )}
                {lead && (
                  <button
                    className="btn small ghost"
                    onClick={() => {
                      setText(tr?.text ?? "");
                      setEditing(lang);
                    }}
                  >
                    Edit
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddQuestion({ setId, engagement, onAdded }: { setId: string; engagement: Engagement; onAdded: () => void }) {
  const { tenant } = useParams();
  const [topic, setTopic] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState<Error | null>(null);
  return (
    <form
      className="row wrap"
      style={{ marginTop: 14 }}
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await api.post(`${t(tenant!)}/engagements/${engagement.id}/question-sets/${setId}/questions`, { topic, text, order: Date.now() % 100000 });
          setTopic("");
          setText("");
          onAdded();
        } catch (err) {
          setError(err as Error);
        }
      }}
    >
      <input className="input" style={{ width: 180 }} placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} required />
      <input className="input" style={{ flex: 1, minWidth: 240 }} placeholder="Question, as you would naturally ask it" value={text} onChange={(e) => setText(e.target.value)} required minLength={3} />
      <button className="btn">Add question</button>
      <ErrorNote error={error} />
    </form>
  );
}
